import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toDateKey } from '../lib/dateKey';

// Throttle marker for touchLifecycleActivity: one lastActiveAt write per uid
// per local day. localStorage may be evicted on native — the only cost is an
// extra Firestore write.
const TOUCH_KEY = 'brainday:lifecycle-touch';

function touchedToday(uid: string): boolean {
  try {
    return localStorage.getItem(TOUCH_KEY) === `${uid}:${toDateKey(new Date())}`;
  } catch {
    return false;
  }
}

function markTouched(uid: string): void {
  try {
    localStorage.setItem(TOUCH_KEY, `${uid}:${toDateKey(new Date())}`);
  } catch {
    /* storage unavailable — retry tomorrow */
  }
}

/**
 * Stamp users/{uid}/lifecycle/main on sign-in so the server-side drip
 * (functions/src/lifecycle) can reach the user. First sign-in seeds signupDate;
 * every sign-in refreshes lastActiveAt. Best-effort: never throws or blocks
 * sign-in.
 */
export async function stampLifecycle(u: {
  uid: string;
  email: string | null;
  displayName: string | null;
  provider: string;
}): Promise<void> {
  if (!u.email) return;
  try {
    const ref = doc(db, 'users', u.uid, 'lifecycle', 'main');
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        email: u.email,
        firstName: u.displayName ?? null,
        provider: u.provider,
        signupDate: serverTimestamp(),
        lastActiveAt: serverTimestamp(),
        unsubscribed: false,
        sent: [],
      });
    } else {
      await setDoc(
        ref,
        {
          email: u.email,
          firstName: u.displayName ?? null,
          provider: u.provider,
          lastActiveAt: serverTimestamp(),
        },
        { merge: true },
      );
    }
    markTouched(u.uid);
  } catch (err) {
    console.warn('stampLifecycle failed', err);
  }
}

/**
 * Refresh lastActiveAt on app open/foreground, at most once per local day.
 * Sessions persist across launches, so the sign-in-time stampLifecycle calls
 * alone froze lastActiveAt at first sign-in — after 14 days every user read as
 * dormant to the drip's segmenting (winback fired for active users, free-active
 * emails were suppressed). Delegates to stampLifecycle so users signed in
 * before the drip shipped get enrolled on their next app open.
 */
export async function touchLifecycleActivity(u: {
  uid: string;
  email: string | null;
  displayName: string | null;
  provider: string;
}): Promise<void> {
  if (!u.email || touchedToday(u.uid)) return;
  await stampLifecycle(u);
}
